import { Component, inject, Input, OnInit } from '@angular/core';
import { Goal2030Model, OutcomeMetricModel, ProjectModel } from '../../../../../../../hub_schema/hubTypes';
import { HubLovService } from '../../../../../../core/services/hub-lov.service';
import { take } from 'rxjs';

@Component({
    selector: 'app-metric-charts',
    templateUrl: './metric-charts.component.html',
    styleUrl: './metric-charts.component.scss',
    standalone: false
})
export class MetricChartsComponent implements OnInit {
    private hubLovService: HubLovService = inject(HubLovService);
    public outcomeMetrics: OutcomeMetricModel[] | undefined;

    @Input()
    public project!: ProjectModel;
    @Input()
    public goal!: Goal2030Model;
    public pomt;

    public ngOnInit(): void {
        this.hubLovService
            .getOutcomeMetrics()
            .pipe(take(1))
            .subscribe((metrics: OutcomeMetricModel[]) => {
                this.outcomeMetrics = metrics;
                this.pomt = this.project.projectOutcomeMetricTargets
                    .map(omt => {
                        var hit;
                        metrics.forEach(metric => {
                            if (metric.outcomeMetricId === omt.outcomeMetricId && metric.goal2030Id === this.goal.goal2030Id) {
                                hit = { ...omt, ...metric, ...this.goal };
                                hit.outcomeMetricName = metric.name;
                            }
                        });
                        return hit;
                    })
                    .filter(hit => !!hit);
            });
    }

    public getOutcomeMetricProgress(outcomeMetricId: number): number {
        var relevantLinkedOutcomes = this.project.linkedOutcomes.filter(lo => lo.projectOutcome.outcome.outcomeMetric.outcomeMetricId === outcomeMetricId);
        const outcomeIdsInGroup = relevantLinkedOutcomes.map(lo => lo.projectOutcome.outcome.outcomeId);

        // M.B. 10-05-2026 filter out double counted outcomes - this occurs when a project outcome link rolls up to a strategy outcome link
        var filteredLinkedOutcomes = relevantLinkedOutcomes.filter(
            a =>
                !a.projectOutcome.outcome?.parentOutcomes.some(
                    po => outcomeIdsInGroup.some(id => id === po.outcomeId) && outcomeIdsInGroup.some(id => id === po.parentOutcomeId)
                )
        );
        var progressTotal = filteredLinkedOutcomes.reduce((acc, currentVal) => {
            let progress = currentVal.projectOutcome.outcomeStatistics.totalProgress;
            if (currentVal.projectOutcome.outcome.outcomeUnit?.outcomeUnitId !== currentVal.projectOutcome.outcome.outcomeMetric.preferredUnitId) {
                var unitConversion = currentVal.projectOutcome.outcome.outcomeUnit.unitConversions.find(
                    ouc =>
                        ouc.sourceUnitId === currentVal.projectOutcome.outcome.outcomeMetric.preferredUnitId &&
                        ouc.targetUnitId === currentVal.projectOutcome.outcome.outcomeUnit?.outcomeUnitId
                );
                progress = progress / unitConversion.conversion;
            }

            return acc + progress;
        }, 0);
        return progressTotal;
    }

    public outcomeMetricPercentageCalculator(outcomeMetricId): number {
        var target = this.pomt.find(p => p.outcomeMetricId === outcomeMetricId);

        return (this.getOutcomeMetricProgress(outcomeMetricId) / target.targetValue) * 100;
    }

    public outcomeMetricRemainderPercentageCalculator(outcomeMetricId): number {
        var progressPercentage = this.outcomeMetricPercentageCalculator(outcomeMetricId);

        return 100 - progressPercentage;
    }

    public displayPreferredOutcomeMetricName() {
        return this.outcomeMetrics.find(om => {
            return this.goal.goal2030Id === om.goal2030.goal2030Id;
        })?.preferredUnit?.name;
    }

    public getGoalClass(goalName: string): string {
        if (goalName === 'Climate Mitigation') {
            return 'climate-mitigation';
        } else if (goalName === 'Climate Adaptation') {
            return 'climate-adaptation';
        } else if (goalName === 'Healthy Oceans') {
            return 'healthy-oceans';
        } else if (goalName === 'Healthy Lands') {
            return 'healthy-lands';
        } else if (goalName === 'Healthy Freshwater') {
            return 'healthy-freshwater';
        } else if (goalName === 'People') {
            return 'people';
        }
        return '';
    }
}
